import React from 'react';
import {
  Typography,
  Tooltip,
  Box,
  Avatar,
  styled
} from '@mui/material';
import { useUserData } from '../../hooks/useUserData';

interface MentionTextProps {
  text: string;
  variant?: 'caption' | 'body1' | 'body2';
  sx?: any;
}

interface MentionData {
  id: string;
  displayName: string;
  startIndex: number;
  endIndex: number;
}

interface TextSegment {
  text: string;
  isMention: boolean;
  mentionData?: MentionData;
}

const MentionSpan = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  }
}));

const UserTooltipContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  minWidth: '200px'
}));

// Function to parse text into segments (mentions and regular text)
const parseTextIntoSegments = (text: string): TextSegment[] => {
  const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
  const segments: TextSegment[] = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    // Add text before the mention
    if (match.index > lastIndex) {
      segments.push({
        text: text.substring(lastIndex, match.index),
        isMention: false
      });
    }

    // Add the mention
    segments.push({
      text: match[1], // Display name only
      isMention: true,
      mentionData: {
        id: match[2],
        displayName: match[1],
        startIndex: match.index,
        endIndex: match.index + match[0].length
      }
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after the last mention
  if (lastIndex < text.length) {
    segments.push({
      text: text.substring(lastIndex),
      isMention: false
    });
  }

  return segments;
};

const UserTooltip: React.FC<{ userId: string; children: React.ReactElement }> = ({ userId, children }) => {
  const { domainUsers } = useUserData();
  const user = domainUsers.find(u => u.id === userId);

  if (!user) {
    return children;
  }

  const getUserInitials = (user: any): string => {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const tooltipContent = (
    <UserTooltipContent>
      <Avatar
        src={user.avatarUrl}
        sx={{
          width: 32,
          height: 32,
          backgroundColor: user.color || '#1976d2',
          fontSize: '0.75rem'
        }}
      >
        {getUserInitials(user)}
      </Avatar>
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'white' }}>
          {`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User'}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
          {user.email}
        </Typography>
      </Box>
    </UserTooltipContent>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement="top"
      arrow
      enterDelay={300}
      leaveDelay={200}
    >
      {children}
    </Tooltip>
  );
};

export const MentionText: React.FC<MentionTextProps> = ({
  text,
  variant = 'caption',
  sx = {}
}) => {
  const textSegments = parseTextIntoSegments(text);

  return (
    <Typography
      variant={variant}
      sx={{
        whiteSpace: 'pre-wrap',
        display: 'block',
        ...sx
      }}
    >
      {textSegments.map((segment, index) => (
        segment.isMention && segment.mentionData ? (
          <UserTooltip key={index} userId={segment.mentionData.id}>
            <MentionSpan>
              {segment.text}
            </MentionSpan>
          </UserTooltip>
        ) : (
          <span key={index}>{segment.text}</span>
        )
      ))}
    </Typography>
  );
};